import { libraryFoldersChangedEvent } from '@lib/events/eventBus';
import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import { buildLibrarySections, LibrarySection } from '@lib/utils/librarySections';
import { showToast } from '@lib/utils/toastmaster';
import {
  getLibraryOmniFields,
  matchesLibraryOmniFilter,
  parseLibraryOmniQuery,
} from '@pages/Libraries/utils/librariesOmniFilter';
import { debugStore } from '@stores/Debug';
import { libraryFavorites } from '@stores/LibraryFavorites';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { Media } from '@typings/Media';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Library, LibraryStore } from '../Library/Library';
import { Renderer } from '../Renderer/Renderer';
import { LibraryManagerMultiLibrarySearch } from './LibraryManagerMultiLibrarySearch';
import { LibraryManagerOptimizedSearch } from './LibraryManagerOptimizedSearch';
const SELECTED_LIBRARIES_TO_SEARCH = 'LibraryManager.selectedLibraries.toSearch';

type ModifyLibraryResponse = {
  success: boolean;
  message: string;
};

/**
 * How much media a library holds that the user actually put there: the total,
 * less the rows that are dead and stay dead.
 *
 * See `librariesWithOwnMedia` for why this subtracts rather than counting the
 * good statuses — briefly, library stats are never refetched when processing
 * completes, so anything keyed on `completed` cannot go true in the session that
 * matters.
 */
function countOwnMedia(stats: Library['stats']): number {
  if (!stats) return 0;
  const dead = (stats.breakdown?.errored?.count ?? 0) + (stats.breakdown?.cancelled?.count ?? 0);
  return Math.max(0, (stats.count ?? 0) - dead);
}

export type LibrarySortBy = 'title' | 'modified' | 'added' | 'duration' | 'count' | 'size';
type LibrarySortDirection = -1 | 1;
export type LibrarySortByHash = `${LibrarySortBy}#${LibrarySortDirection}`;
const LIBRARY_SORT_BY_HASH = 'librarySortByHash';
// Deliberately localStorage, alongside the sort hash it modifies — not user settings.
// `/user/me/settings` takes a whole document, and a third writer of it re-opens the clobber
// bug documented at `UIPreferences.ts:245-275`.
const LIBRARY_PINNED_FIRST = 'libraryPinnedFirst';

export class LibraryManager extends ReactionManager {
  readonly multiLibSearch: LibraryManagerMultiLibrarySearch;
  readonly optimizedSearch: LibraryManagerOptimizedSearch;
  readonly renderer: Renderer;

  @observable
  selectedLibraryIdsToSearch = new Set<string>();

  /**
   * Set by the auto-prune to tell the persist reaction that the next scope change is
   * our housekeeping, not the user changing what they search.
   *
   * **Consumed by the reader, not cleared by the writer.** A `try/finally` around the
   * prune looks tidier and does not work: MobX runs reaction effects inside a batch,
   * so the nested action's `endAction` does not flush, and the persist reaction fires
   * only when the outer batch ends — by which time a `finally` would already have
   * cleared this. Clearing it where it is read is correct whatever the batching does.
   *
   * Deliberately not observable; nothing should re-render on it.
   */
  private _pruningSearchScope = false;

  @observable
  selectedLibraryIdToView = '';

  @observable
  _libraries = new Map<string, LibraryStore>();

  @observable
  _sortBy: LibrarySortBy = 'modified';

  // The libraries "omnifilter" query. Space-separated tokens; a library is shown when
  // its name/ID matches per the match mode below. See utils/librariesOmniFilter.ts.
  @observable
  _omniFilter: string | null = null;

  @observable
  _sortDirection: 1 | -1 = 1;

  // Whether pinned libraries are lifted above the active sort. Defaults to ON — it's the
  // behaviour the feature shipped with; only an explicit opt-out is stored.
  @observable
  _pinnedFirst: boolean = localStorage.getItem(LIBRARY_PINNED_FIRST) !== 'false';

  @observable
  _multiLibrarySelection: boolean = false;

  @action
  setSorting(sortByHash: LibrarySortByHash) {
    const [sortBy, sortDirection] = sortByHash.split('#');
    this._sortBy = sortBy as LibrarySortBy;
    this._sortDirection = parseInt(sortDirection) as LibrarySortDirection;
    localStorage.setItem(LIBRARY_SORT_BY_HASH, sortByHash);
  }

  @action
  setOmniFilter(query: string | null) {
    this._omniFilter = query?.trim() || null;
  }

  @action
  setPinnedFirst(pinnedFirst: boolean) {
    this._pinnedFirst = pinnedFirst;
    localStorage.setItem(LIBRARY_PINNED_FIRST, String(pinnedFirst));
  }

  /**
   * Whether the Libraries surfaces lift pinned libraries above the active sort — and so also
   * whether they draw "Pinned" / "Other libraries" sections, since those exist to explain the
   * lift. Off means a strict sort with no sections; the pin glyph still shows, because a pin
   * is also a Workspace bookmark, it just stops implying position.
   */
  @computed
  get pinnedFirst() {
    return this._pinnedFirst;
  }

  @computed
  get sortByHash(): LibrarySortByHash {
    return `${this._sortBy}#${this._sortDirection}`;
  }

  /** Whether a libraries omni-filter query is active. */
  @computed
  get hasOmniFilter() {
    return !!this._omniFilter;
  }

  @computed
  get libraries() {
    return Array.from(this._libraries.values()).filter(
      (l): l is LibraryStore & { data: NonNullable<LibraryStore['data']> } => l.data !== null,
    );
  }

  /**
   * Every library id the manager holds, *including* ones whose `data` is momentarily null.
   *
   * `libraries` above filters those out, which makes it the wrong source for anything that
   * reads absence as deletion: `LibraryStore` is a `DataFrame` with the default
   * `keepDataOnFetchError = false`, so any failed refresh nulls `data` (`dataframe.ts:95-97`)
   * and drops the library from that computed until the next successful fetch. A library only
   * leaves this map when the backend stops listing it.
   */
  @computed
  get knownLibraryIds(): Set<string> {
    return new Set(this._libraries.keys());
  }

  @action
  setMultiLibrarySelection(multiLibrarySelection: boolean) {
    this._multiLibrarySelection = multiLibrarySelection;

    if (!multiLibrarySelection && this.selectedLibraryIdsToSearch.size > 1) {
      const firstLibraryId = [...this.selectedLibraryIdsToSearch][0];
      this.setSelectedLibraries([firstLibraryId]);
    }
  }

  @computed
  get multiLibrarySelection() {
    return this._multiLibrarySelection;
  }

  /**
   * The single grouping+ordering derivation every Libraries surface renders — desktop grid and
   * mobile grid (`LibraryCards`), mobile list (`LibraryList`) and the desktop table
   * (`LibrariesTable`, which runs MUI in `sortingMode="server"` and so never re-sorts). All
   * three therefore inherit this contract, in three stages:
   *
   *   filter (omnifilter) → sort (active sort mode) → group pinned first, when `pinnedFirst`
   *
   * Grouping last is what makes pinning an *ordering* rule rather than a membership one: a
   * pinned library the omnifilter excluded stays hidden, and pinned survivors sit on top of a
   * block that is still internally in the active sort order.
   *
   * Only pin *membership* is read, never pin recency — the requirement is that the active
   * sort still applies inside the pinned block. Recency order stays a Workspace concern.
   *
   * Sections carry a semantic id, not a label: the copy is the renderer's business
   * (`LibrarySectionLabel`). With "Pinned first" off this yields one unlabelled section, which
   * is how the surfaces know to draw no headers.
   */
  @computed
  get librarySections(): LibrarySection<LibraryStore>[] {
    let libraryStores = Array.from(this._libraries.values()).filter(
      (l): l is LibraryStore & { data: NonNullable<LibraryStore['data']> } => l.data !== null,
    );

    const parsed = parseLibraryOmniQuery(this._omniFilter, omniFilterPreferences.matchAll);
    if (parsed) {
      libraryStores = libraryStores.filter((l) =>
        matchesLibraryOmniFilter(getLibraryOmniFields(l), parsed),
      );
    }

    if (this._sortBy === 'title') {
      libraryStores.sort((a, b) => a.data.name.localeCompare(b.data.name) * this._sortDirection);
    } else if (this._sortBy === 'modified') {
      libraryStores.sort((a, b) => (b.data.updatedAt - a.data.updatedAt) * this._sortDirection);
    } else if (this._sortBy === 'added') {
      libraryStores.sort((a, b) => (b.data.createdAt - a.data.createdAt) * this._sortDirection);
    } else if (this._sortBy === 'duration') {
      libraryStores.sort((a, b) => {
        return (
          ((b.data.stats?.mediaDuration || 0) - (a.data.stats?.mediaDuration || 0)) *
          this._sortDirection
        );
      });
    } else if (this._sortBy === 'count') {
      // `stats` can legitimately be absent (it arrives inline on /library, but a
      // store can be constructed before it loads), so missing counts read as 0
      // and settle at the bottom of a descending sort — same guard as duration.
      libraryStores.sort((a, b) => {
        return ((b.data.stats?.count || 0) - (a.data.stats?.count || 0)) * this._sortDirection;
      });
    } else if (this._sortBy === 'size') {
      // Same missing-stats guard as above.
      libraryStores.sort((a, b) => {
        return (
          ((b.data.stats?.mediaSize || 0) - (a.data.stats?.mediaSize || 0)) * this._sortDirection
        );
      });
    }

    return buildLibrarySections(libraryStores, {
      pinnedFirst: this._pinnedFirst,
      // Only read behind the `pinnedFirst` check inside `buildLibrarySections`, so this computed
      // does not subscribe to the pin set at all while the preference is off — a pin toggle then
      // invalidates nothing.
      isPinned: (l) => libraryFavorites.favoriteIdSet.has(l.libraryId),
    });
  }

  /**
   * The flat order every surface renders — the sections above, concatenated.
   *
   * Derived from `librarySections` rather than the other way round, so a section header can never
   * disagree with the sequence beneath it. It used to be the primary computed, with each of the
   * three surfaces re-splitting it to recover the boundary the hoist had just erased; that made
   * their agreement a property to unit-test instead of a fact, and had the pin predicate spelled
   * two different ways across the surfaces.
   */
  @computed
  get sortedAndFilteredLibraries(): LibraryStore[] {
    return this.librarySections.flatMap((section) => section.items);
  }

  @computed
  get areLibraryStatsLoaded() {
    return this.libraries.every((l) => l.areStatsLoaded) && this._libraries.size > 0;
  }

  @computed
  get defaultLibraryId(): string | null {
    // Once default library API endpoint is available, return the default library.
    // For now, return the most recently viewed library.
    if (this.defaultLibrary) return this.defaultLibrary.libraryId;
    return null;
  }

  @computed
  get defaultLibrary() {
    const defaultLibraries = Array.from(this._libraries.values()).filter(
      (l) => l.data?.system?.default,
    );
    if (defaultLibraries.length === 0) return null;
    if (defaultLibraries.length > 1)
      console.warn('More than one default library detected in this account.');
    const defaultLibrary = defaultLibraries[0];
    return defaultLibrary;
  }

  @computed
  get demoLibrary() {
    return this.libraries.find((l) => l.data?.system?.demo);
  }

  /**
   * The libraries holding media the user has actually committed — everything
   * non-demo whose total minus its dead rows is above zero. Derived from the
   * already-loaded `_libraries` map (populated by getAllLibraries) — no extra
   * API call.
   *
   * Subtracting the dead rows rather than counting the good ones is deliberate,
   * and the reason is staleness. Library stats are only refetched by
   * `getAllLibraries` (login) and `refreshLibrary` (upload / import / delete) —
   * **nothing refetches them when backend processing finishes.** So a predicate
   * keyed on `breakdown.completed` cannot become true until something unrelated
   * happens to refresh the library, which in a session where you upload once and
   * wait is never: the user's first upload lands, processes, appears in their
   * library, and the workspace still shows the getting-started layout. Keying on
   * the total instead flips at upload finish, which is exactly when
   * `refreshLibrary` fires.
   *
   * `errored` and `cancelled` are subtracted because they are the rows that
   * persist — never auto-reclaimed, waiting on the user to clear them — so a
   * single failed upload would otherwise pass for a library of media. Transient
   * states (`empty`, `uploading`, `queued`, `processing`) are left in: they mean
   * media is on its way, and each resolves on its own.
   *
   * Falls through to the raw `count` when `breakdown` is absent or empty, since
   * subtracting nothing is the right answer there — no special case needed.
   * An unloaded `stats` counts as nothing, which is the safe direction:
   * `isFirstTimeUser` would rather greet a returning user as a newcomer for the
   * moment before stats land than nudge a newcomer past the getting-started
   * layout for good.
   */
  @computed
  get librariesWithOwnMedia(): LibraryStore[] {
    return this.libraries.filter((l) => !l.data?.system?.demo && countOwnMedia(l.stats) > 0);
  }

  /**
   * The user's most recently updated library that actually has *usable* media,
   * excluding the demo. `updatedAt` = last modified (no per-library "last
   * viewed" exists).
   *
   * Keyed on `completed` rather than on `librariesWithOwnMedia`, because this
   * answers a different question. That one asks "has this user committed any
   * media" — a classification, which wants to be true the moment they upload.
   * This one picks a library to *offer*, and the card it feeds lets you search
   * the library or open it with an agent, neither of which works on media that
   * is still processing. Offering it would be a dead end.
   *
   * The stats staleness that shapes `librariesWithOwnMedia` does not bite here:
   * this getter only ever needs to *exclude*, and a library whose completed
   * count is stale at 0 is simply passed over in favour of one that is ready.
   *
   * The media check also stops a brand-new empty library — whose `updatedAt` is
   * bumped by its own creation — from hijacking the "most recent" slot. Null
   * when no non-demo library has finished media yet.
   */
  @computed
  get latestLibrary(): LibraryStore | null {
    const candidates = this.libraries.filter(
      (l) => !l.data?.system?.demo && (l.stats?.breakdown?.completed?.count ?? 0) > 0,
    );
    if (candidates.length === 0) return null;
    return candidates.reduce((latest, l) =>
      (l.data?.updatedAt ?? 0) > (latest.data?.updatedAt ?? 0) ? l : latest,
    );
  }

  async forceFetchDefaultLibrary(tries = 0): Promise<LibraryStore> {
    if (tries > 3) console.error('Failed to fetch default library, need to refresh page.');
    await this.getAllLibraries();
    if (!this.defaultLibrary) {
      await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, tries)));
      return this.forceFetchDefaultLibrary(tries + 1);
    }
    return this.defaultLibrary;
  }

  @computed
  get selectedLibrariesToSearch() {
    return Array.from(this._libraries.values())
      .filter(
        (l): l is LibraryStore & { data: NonNullable<LibraryStore['data']> } => l.data !== null,
      )
      .filter((l) => this.selectedLibraryIdsToSearch.has(l.libraryId))
      .filter((l) => this._libraries.has(l.libraryId));
  }

  /**
   * Selected search ids with no library of ours behind them — the ids
   * `selectedLibrariesToSearch` above silently drops.
   *
   * They are reachable in normal use, not a theoretical case. The selection is
   * persisted to localStorage and rehydrated without validation (see the
   * constructor, where a comment has promised an access check since day one), so
   * deleting a library, or having one unshared, leaves its id in the scope
   * forever. The search then runs against a smaller scope than the picker
   * implies, and before the Moments empty state existed the result was a blank
   * page.
   *
   * We cannot say *which* it is. `GET /library` returns only libraries you are a
   * member of, so a deleted library and someone else's are equally absent; the
   * search endpoint is no more forthcoming — its access check is a membership
   * test, so both would answer 403 `user.library.forbidden`. Hence the hedge in
   * the copy this feeds.
   *
   * Empty until `areLibraryStatsLoaded`, which is the closest thing to a
   * "library list has landed" flag (`getAllLibraries` sets no such flag and never
   * prunes). Without that gate every id looks unreachable during boot and the
   * empty state would accuse perfectly good libraries.
   *
   * Tested against `knownLibraryIds`, NOT `libraryById(id)?.data`. `LibraryStore`
   * is a `DataFrame` with the default `keepDataOnFetchError = false`, so any failed
   * refresh — a 500, a flaky connection — nulls `data` and would make a perfectly
   * good library read as deleted. An id only leaves the map when the backend stops
   * listing it, which is the fact this getter is actually about. That matters twice
   * over here, because the prune keyed off this **persists**: getting it wrong would
   * drop a good library from the scope for good over one network blip.
   *
   * Normally **transient**: the pruning reaction in the constructor empties this as
   * soon as it becomes non-empty, so don't expect a dead id to sit here. It is the
   * signal for that prune, not a durable list. What survives in the scope, and what
   * the Moments `unreachable` state therefore mostly describes, is a library that
   * still exists but answers 403 — see `forbiddenLibrariesInScope`.
   */
  @computed
  get unreachableLibraryIdsToSearch(): string[] {
    if (!this.areLibraryStatsLoaded) return [];
    return Array.from(this.selectedLibraryIdsToSearch).filter(
      (id) => !this.knownLibraryIds.has(id),
    );
  }

  /**
   * Whether the user has no media of their own **right now** — the signal the
   * Workspace bento order and the Moments empty-state copy both key off.
   *
   * Note the tense: this is a has-right-now fact, not has-ever, and that is
   * deliberate. Emptying your last library returns you to the getting-started
   * experience, and adding media takes you straight back out of it. There is no
   * latch. (Nor could there be a cheap one: the old `libraries.length > 2` clause
   * happened to act as a one-way ratchet, but it was also the bug — see below.)
   *
   * Decided by media, never by library count. The old test tripped on library
   * count alone, so creating a single library — or visiting the podcast clipper,
   * which provisions one for you — pushed a user who had uploaded nothing into
   * the returning layout. `system` carries only `demo` / `readonly` / `default`
   * (`Library.ts`), so a provisioned library cannot be filtered out by kind, and
   * filtering on `isLibraryTranscriptOnly` would wrongly drop a genuine
   * transcript-only library that does have media. Counting media sidesteps all of
   * it. The old companion clause only ever looked at `defaultLibrary`, so media
   * kept anywhere else read as first-time regardless.
   *
   * The demo library is excluded by `librariesWithOwnMedia`: it arrives full, and
   * counting it would mean nobody was ever a first-timer.
   */
  @computed
  get isFirstTimeUser() {
    if (debugStore.respectfulDebugMode && debugStore.forceFirstTimeUser) {
      return true;
    }
    return this.librariesWithOwnMedia.length === 0;
  }

  constructor() {
    super();
    makeObservable(this);
    this.multiLibSearch = new LibraryManagerMultiLibrarySearch(this);
    this.optimizedSearch = new LibraryManagerOptimizedSearch(this);
    this.renderer = new Renderer();
    const sortByHash = localStorage.getItem(LIBRARY_SORT_BY_HASH) || 'modified#1';
    this.setSorting(sortByHash as LibrarySortByHash);

    // Every folder write answers with the library's whole tree, whoever made it — the library
    // page, or the upload pipeline filing a dropped directory.
    this._reactions.push(
      libraryFoldersChangedEvent.on(({ libraryId, folders }) => {
        this.libraryById(libraryId)?.setFolders(folders);
      }),
    );

    this.reaction(
      () => userStore.isLoggedIn,
      (isLoggedIn) => {
        if (isLoggedIn) {
          this.getAllLibraries();
        } else {
          // Clear library data on logout
          runInAction(() => {
            this._libraries.forEach((l) => l.deinit());
            this._libraries.clear();
            this.selectedLibraryIdsToSearch.clear();
            this.selectedLibraryIdToView = '';
            this.multiLibSearch.clearSearch();
            this.optimizedSearch.clearSearch();
          });
        }
      },
      { fireImmediately: true },
    );

    // load selected libraries from local storage
    const selectedLibrariesToSearch = localStorage.getItem(SELECTED_LIBRARIES_TO_SEARCH);
    if (selectedLibrariesToSearch) {
      const selectedLibrariesList = JSON.parse(selectedLibrariesToSearch);
      runInAction(() => {
        for (const libraryId of selectedLibrariesList) {
          // check if we have access to the library
          this.selectedLibraryIdsToSearch.add(libraryId);
        }
      });
    }

    this.reaction(
      () => JSON.stringify(Array.from(this.selectedLibraryIdsToSearch)),
      (newSelectedLibraries) => {
        localStorage.setItem(
          SELECTED_LIBRARIES_TO_SEARCH,
          JSON.stringify(Array.from(this.selectedLibraryIdsToSearch)),
        );
        // The clip / media resets below mean "you changed what you're searching, so a
        // clip-scoped search no longer makes sense". A prune is not that: it removes
        // libraries that were already contributing nothing, so it must not drag the
        // scope down with it.
        //
        // Without this guard, opening a shared clip- or media-scoped search URL loses
        // that scope about one round trip after load — as soon as `/library` returns
        // and the prune fires over a single stale id in localStorage — silently
        // downgrading it to a plain text search.
        if (this._pruningSearchScope) {
          this._pruningSearchScope = false;
          return;
        }
        if (this.multiLibSearch.searchClipPreview) {
          this.multiLibSearch.setSearchClip(null, null, false);
        }
        if (this.multiLibSearch.mediaId) {
          this.multiLibSearch.setMediaId(null);
        }
      },
    );

    // Seed an empty scope with the demo library once the library list lands. This is
    // the original fallback and it covers the cases where nothing is selected
    // *because nothing ever was*: a brand-new account, cleared storage, or a
    // logout→login in the same tab (the logout branch above clears the set).
    //
    // Keyed on the demo's arrival rather than on the selection, so it fires once per
    // session and stays off the deselect-everything path. That path belongs to
    // `LibrarySwitch.handleClose`, which restores the demo with a
    // `library-no-selection` toast when the picker closes — reacting to the
    // selection emptying would beat it there, re-selecting the demo row while the
    // picker is still open and suppressing the toast that explains it.
    this.reaction(
      () => this.demoLibrary !== undefined,
      () => {
        if (this.demoLibrary && this.selectedLibraryIdsToSearch.size === 0) {
          this.selectLibraryToSearch(this.demoLibrary.libraryId);
        }
      },
    );

    // Forget libraries the backend has stopped listing — deleted, or unshared —
    // instead of keeping their ids in the scope forever.
    //
    // Nothing pruned the persisted selection, and the whole failure mode is that it
    // stays silent: the id survives while everything that resolves it to a store
    // goes through the *filtered* derived list, so the scope quietly searches less
    // than the picker implies. It never throws. Symptoms range from the Select-all
    // control reading "Deselect all" when nothing appears selected (`isBulkDeselect`
    // reads the raw `.size`) to `switchLabel` falling through to `''` for a bare
    // chevron once every id is dead. And it survives reload, because hydration
    // re-adds every persisted id unchecked — under a comment that has claimed to
    // check access since day one.
    //
    // Same shape as the pin pruning on the Libraries page
    // (`LibraryFavorites.pruneMissing` + its `useFavoriteLibraries` driver), which
    // this deliberately copies:
    //
    //  - Pruned against `knownLibraryIds`, **not** `libraries`. The latter filters on
    //    `data !== null`, which one failed refresh is enough to trigger
    //    (`keepDataOnFetchError` is false) — and because pruning *persists*, that
    //    would drop a good library from the scope for good over a network blip.
    //    `unreachableLibraryIdsToSearch` reads the map, and is empty until stats land
    //    so an unloaded list is never mistaken for "everything was deleted".
    //  - A reaction rather than a one-shot filter, because hydration runs before
    //    `/library` returns, and because re-running is what cleans up deletions that
    //    happen mid-session.
    //
    // Keyed on the joined ids, not the array: the computed returns a fresh array
    // every recomputation, so reference equality would re-fire this constantly.
    //
    // Silent by design when it prunes part of a scope — the scope simply forgets what
    // you deleted. `dropLibrariesFromSearch` reports whether it had to fall back to
    // the demo, and only that case gets a toast, because only that case changes what
    // the user is searching rather than tidying what they aren't.
    this.reaction(
      () => this.unreachableLibraryIdsToSearch.join(','),
      () => {
        const gone = this.unreachableLibraryIdsToSearch;
        if (gone.length === 0) return;
        // Tells the persist reaction its clip/media resets don't apply to this change;
        // it clears the flag itself when it reads it. Safe to set unconditionally here
        // because we only reach this line when ids are actually about to be removed,
        // so that reaction is certain to run and consume it.
        this._pruningSearchScope = true;
        if (this.dropLibrariesFromSearch(gone)) {
          showToast('library-unavailable');
        }
      },
    );

    // @ts-ignore
    window.libraryManager = this;
  }

  override deinit() {
    super.deinit();
    this._libraries.forEach((l) => l.deinit());
    this._libraries.clear();
    this.multiLibSearch.deinit();
    this.optimizedSearch.deinit();
  }

  libraryById(libraryId: string) {
    return this._libraries.get(libraryId);
  }

  /** True when the media's library skips visual indexing (no visual descriptions). */
  isMediaTranscriptOnly(media: Media): boolean {
    return !!this.libraryById(media.libraryId)?.isTranscriptOnly;
  }

  async getAllLibraries() {
    const libraries = await userStore.apiWithAuth<Library[]>('/library');
    runInAction(() => {
      for (const library of libraries) {
        this._libraries.set(library._id, new LibraryStore(library._id, library));
      }
    });
  }

  @action
  setLibraryToView(libraryId: string | null) {
    // check if valid library id before assigning
    if (libraryId && this._libraries.has(libraryId)) {
      this.selectedLibraryIdToView = libraryId;
    } else {
      // Select last library in list as default.
      this.selectedLibraryIdToView =
        this.libraries.length > 0 ? this.libraries[this.libraries.length - 1].libraryId : '';
    }
  }

  @action
  setSelectedLibraries(libraries: string[]) {
    this.selectedLibraryIdsToSearch.clear();
    if (libraries.length > 1) {
      this.setMultiLibrarySelection(true);
    }
    for (const libraryId of libraries) {
      this.selectedLibraryIdsToSearch.add(libraryId);
    }
  }

  @action
  selectLibraryToSearch(libraryId: string) {
    this.selectedLibraryIdsToSearch.add(libraryId);
  }

  @action
  deselectLibraryToSearch(libraryId: string) {
    this._libraries.get(libraryId)?.search.clearSearch();
    this.selectedLibraryIdsToSearch.delete(libraryId);
  }

  /**
   * Take libraries out of the search scope *on the user's behalf* — ones that were
   * deleted, or that the search couldn't reach — and leave the scope searchable.
   *
   * Deliberately separate from `deselectLibraryToSearch`, and the fallback is the
   * whole difference. Plain deselection is the user curating their own scope, where
   * ending up with nothing selected is a legitimate thing to do and is already
   * handled: `LibrarySwitch`'s close handler restores the demo with a toast when
   * the picker closes. This is us doing the removing, from a surface that is not
   * the picker — so nothing downstream would ever restore it and the scope would
   * sit empty behind a blank trigger.
   *
   * The reaction in the constructor can't cover it either: it requires
   * `selectedLibraryIdsToSearch.size > 0`, precisely so it stays off the
   * deselect-everything path, and removing the last id takes the size to 0. A
   * forbidden library is the case that gets here — it resolves fine (it has a
   * store, its search just 403s), so the reaction had no reason to add the demo,
   * and then it is the only thing in the scope.
   *
   * Being an `@action` matters: the intermediate states are batched, so the
   * reaction never observes a transient empty scope mid-loop.
   *
   * @returns whether it had to fall back to the demo library. Callers that removed
   * libraries on their own initiative use this to decide whether to explain
   * themselves — tidying a scope needs no announcement, changing what the user is
   * searching does.
   */
  @action
  dropLibrariesFromSearch(libraryIds: string[]): boolean {
    for (const libraryId of libraryIds) {
      this.deselectLibraryToSearch(libraryId);
    }
    const demo = this.demoLibrary;
    // Don't re-add a demo the caller just asked us to remove — if the demo library
    // is itself the unsearchable one (its own search 403s), re-selecting it would
    // make "Remove it from search" a visible no-op.
    if (!demo || libraryIds.includes(demo.libraryId)) return false;
    // Tested on the raw id count, NOT on `selectedLibrariesToSearch.length`. That
    // list filters on `data !== null`, so a library whose last refresh happened to
    // fail reads as absent — and the scope would silently *gain* the demo library
    // over one flaky request while the user's own selection was still sitting there.
    // The question here is only "did we just empty the scope", which is the raw set.
    if (this.selectedLibraryIdsToSearch.size === 0) {
      this.selectLibraryToSearch(demo.libraryId);
      return true;
    }
    return false;
  }

  @action
  toggleLibraryToSearchSelection(libraryId: string) {
    if (this.selectedLibraryIdsToSearch.has(libraryId)) {
      this.deselectLibraryToSearch(libraryId);
    } else {
      if (this.multiLibrarySelection) {
        this.selectLibraryToSearch(libraryId);
      } else {
        this.setSelectedLibraries([libraryId]);
      }
    }
    trackingStore.searchLibraryChanged({
      libraryIds: Array.from(this.selectedLibraryIdsToSearch),
      libraryCount: this.selectedLibraryIdsToSearch.size,
    });
  }

  @action
  selectAllLibrariesToSearch() {
    this.selectedLibraryIdsToSearch.clear();
    this.libraries.forEach((l) => this.selectedLibraryIdsToSearch.add(l.libraryId));
  }

  @action
  deselectAllLibrariesToSearch() {
    this.selectedLibraryIdsToSearch.clear();
  }

  async createNewLibrary(libraryName: string, settings?: Partial<Library['settings']['v1']>) {
    const { library } = await userStore.apiWithAuth<{ library: Library }>(`/library`, {
      method: 'POST',
      body: {
        name: libraryName,
        // `v1` is left blank by default so the server picks the search engines; callers
        // pass whatever create-time settings they expose (e.g. `{ skip: { visuals: true } }`
        // for a transcript-only library — set at create, not changeable later).
        settings: { v1: { ...settings } },
      },
    });

    library.stats = {
      count: 0,
      mediaSize: 0,
      mediaDuration: 0,
      breakdown: {},
    };
    library.latestMediaIds = [];

    runInAction(() => {
      this._libraries.set(library._id, new LibraryStore(library._id, library));
    });

    trackingStore.libraryCreated({ libraryId: library._id, libraryName: libraryName });

    return library;
  }

  /** Rejects when the delete does not go through, for any reason; the caller owns the failure toast. */
  @action
  async deleteLibrary(libraryId: string): Promise<ModifyLibraryResponse> {
    const response = (await userStore.apiWithAuth(`/library/${libraryId}`, {
      method: 'DELETE',
    })) as ModifyLibraryResponse;
    // Take it out of the search scope *before* the store goes, so the pruning
    // reaction never sees it. Not about the pruning itself — the reaction would
    // catch this id the moment `_libraries` loses it — but about what the reaction
    // says while doing it: falling back to the demo makes it toast "That library is
    // no longer available", in the error variant, which is a strange thing to read
    // immediately after deliberately deleting the library you were searching. Doing
    // it here means the same clean-up happens silently, because the user already
    // knows.
    this.dropLibrariesFromSearch([libraryId]);
    this._libraries.get(libraryId)?.deinit();
    this._libraries.delete(libraryId);
    trackingStore.libraryDeleted({ libraryId });
    return response;
  }

  @action
  async modifyLibrary(
    libraryId: string,
    newName: string,
    newThumbnailMediaId: string,
    glossary?: string[],
  ): Promise<ModifyLibraryResponse | null> {
    try {
      const userMetadata: Record<string, any> = {};
      if (newThumbnailMediaId !== '') {
        userMetadata.thumbnailMediaId = newThumbnailMediaId;
      }

      const body: Record<string, any> = {
        name: newName,
        userMetadata,
      };

      // Add settings if glossary is provided
      if (glossary !== undefined) {
        body.settings = {
          v1: {
            glossary,
          },
        };
      }

      const response = (await userStore.apiWithAuth(`/library/${libraryId}`, {
        method: 'PATCH',
        body,
      })) as ModifyLibraryResponse;
      trackingStore.libraryRenamed({ libraryId, newName });
      runInAction(() => {
        const library = this._libraries.get(libraryId);
        if (library && library.data) {
          library.data.name = newName;
          const updatedMetadata = { ...(library.data.userMetadata || {}) };
          if (newThumbnailMediaId === '') {
            delete updatedMetadata.thumbnailMediaId;
          } else {
            updatedMetadata.thumbnailMediaId = newThumbnailMediaId;
          }
          library.data.userMetadata = updatedMetadata;

          // Update settings if glossary was provided
          if (glossary !== undefined) {
            library.data.settings = {
              ...library.data.settings,
              v1: {
                ...library.data.settings.v1,
                glossary,
              },
            };
          }

          // `updatedAt` is deliberately not touched here. The backend owns it and
          // this PATCH's response carries only {success, message}, so anything set
          // locally is a guess. It used to be set to `Date.now() + 24h` to force the
          // library to the top of a "Last modified" sort — harmless while the value
          // was sort-only, but the Libraries list view now renders it as an "Updated"
          // column, where a future timestamp reads as "Just now" for a whole day and
          // then under-reports the age by one. The real value arrives with the next
          // getAllLibraries(); until then the last known one is shown, which is at
          // least true. If jumping to the top of that sort is wanted again, it needs a
          // separate local boost field rather than corrupting a displayed value.
        }
      });
      return response;
    } catch (error) {
      console.error(error);
    }
    return null;
  }
}

export const { Provider: LibraryManagerProvider, useStore: useLibraryManager } = createContext(
  LibraryManager,
  {
    dispose: (store) => store.deinit(),
  },
);
